import React from 'react';
import type { Page, Component } from '../ComponentEditModal.types';

interface ContentAreaData {
  label?: string;
  minHeight?: number;
  maxWidth?: string;
  centered?: boolean;
  backgroundColor?: string;
  padding?: {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  };
}

interface ContentAreaEditorProps {
  formData: ContentAreaData;
  setFormData: (data: ContentAreaData) => void;
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
}

export const ContentAreaEditor: React.FC<ContentAreaEditorProps> = ({ formData, setFormData }) => {
  const data = formData;

  return (
    <div className="space-y-8">
      {/* Content Area Settings */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Content Area Settings</h3>
        <p className="text-sm text-gray-600 mb-4">
          The Content Area marks where page-specific content will be rendered when this layout is
          applied to a page.
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
            <input
              type="text"
              value={data.label || 'Page Content'}
              onChange={e => setFormData({ ...data, label: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="Page Content"
            />
            <p className="mt-1 text-xs text-gray-500">
              This label is shown in the editor to identify the content area
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum Height (px)
            </label>
            <input
              type="number"
              value={data.minHeight || 200}
              onChange={e => setFormData({ ...data, minHeight: parseInt(e.target.value) || 200 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              min="0"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Max Width</label>
            <select
              value={data.maxWidth || 'container'}
              onChange={e => setFormData({ ...data, maxWidth: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="full">Full Width</option>
              <option value="container">Container (max-w-7xl)</option>
              <option value="narrow">Narrow (max-w-4xl)</option>
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="centered"
              checked={data.centered !== false}
              onChange={e => setFormData({ ...data, centered: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="centered" className="text-sm font-medium text-gray-700">
              Center content horizontally
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Background Color</label>
            <input
              type="text"
              value={data.backgroundColor || 'transparent'}
              onChange={e => setFormData({ ...data, backgroundColor: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="transparent"
            />
            <p className="mt-1 text-xs text-gray-500">
              Use "transparent" or any valid CSS color (e.g., #f3f4f6, white)
            </p>
          </div>
        </div>
      </div>

      {/* Padding Settings */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Padding</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Top (px)</label>
            <input
              type="number"
              value={data.padding?.top || 0}
              onChange={e =>
                setFormData({
                  ...data,
                  padding: { ...data.padding, top: parseInt(e.target.value) || 0 },
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              min="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Bottom (px)</label>
            <input
              type="number"
              value={data.padding?.bottom || 0}
              onChange={e =>
                setFormData({
                  ...data,
                  padding: { ...data.padding, bottom: parseInt(e.target.value) || 0 },
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              min="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Left (px)</label>
            <input
              type="number"
              value={data.padding?.left || 0}
              onChange={e =>
                setFormData({
                  ...data,
                  padding: { ...data.padding, left: parseInt(e.target.value) || 0 },
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              min="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Right (px)</label>
            <input
              type="number"
              value={data.padding?.right || 0}
              onChange={e =>
                setFormData({
                  ...data,
                  padding: { ...data.padding, right: parseInt(e.target.value) || 0 },
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              min="0"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
